import React, { Dispatch, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button, Checkbox } from '@xsolla/ui-core';
import { Checkmark, Pencil } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import { resetEmailConfirmation, setUserEmail } from '../../../../api';
import { useNotifications } from '../../../../hooks/useNotifications';
import { validateEmail } from '../../../../utils';
import { ExtraInputButton, FieldInput } from '../../../ui';
import { UserFieldWrapper } from '../style';
import { Action, ActionType, UserState } from '../types';
import { ForceUpdateModal } from './ForceUpdateModal';

type Props = {
  state: UserState;
  projectId: string;
  dispatch: Dispatch<Action>;
};

export const CheckboxLabel = styled.a`
  ${(props) => props.theme.typography.description}
  color: ${(props) => props.theme.colors.muted};
`;

const ChangeEmail = ({ state, projectId, dispatch }: Props): JSX.Element => {
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const [skipConfirmationEmail, setSkipConfirmationEmail] = useState(false);

  const {
    newEmail,
    user,
    emailEdit,
    emailError,
    resendEmailFetching,
    emailTaken,
    newEmailFetching,
    isChangingEmailAllowed
  } = state;

  const [onOpenForceUpdateModal, onCloseForceUpdateModal] = useModal(
    () => (
      <ForceUpdateModal
        email={newEmail}
        emailTaken={emailTaken}
        fetching={newEmailFetching}
        dispatch={dispatch}
        projectId={projectId}
        userId={user.id || ''}
        onClose={onCloseForceUpdateModal}
      />
    ),
    [emailTaken, newEmailFetching]
  );

  const setEmailHandle = async (email: string) => {
    if (!user || !user.id) return;

    try {
      dispatch({ type: ActionType.changeEmailStart });
      const response = await setUserEmail(projectId, user.id, {
        email,
        email_confirmation_needed: !skipConfirmationEmail,
        force_unlink: !!skipConfirmationEmail
      });
      if (response.email_taken && !response.email_set) {
        dispatch({ type: ActionType.changeEmailForceUnlink, payload: response.email_taken });
        onOpenForceUpdateModal();
      } else {
        showSuccessNotify({
          details:
            intl.messages['users.settings.contact-info.primary-email.resend.sucesss'].toString()
        });
      }
    } catch (err) {
      showErrorNotify(err);
    } finally {
      dispatch({ type: ActionType.changeEmailEnd });
    }
  };

  const resendConfirmationHandler = async () => {
    if (user.id) {
      try {
        dispatch({ type: ActionType.resendEmailStart });
        await resetEmailConfirmation(projectId, user.id);
        showSuccessNotify();
      } catch (err) {
        showErrorNotify(err);
      } finally {
        dispatch({ type: ActionType.resendEmailEnd });
      }
    }
  };

  return (
    <UserFieldWrapper>
      <FieldInput
        name="email"
        onChange={(e) => {
          dispatch({ type: ActionType.changeEmail, payload: e.target.value });
        }}
        value={newEmail}
        maxLength={120}
        ignoreErrorMeta
        additional={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <Button
            look="link"
            palette="brand"
            size="md"
            type="button"
            fetching={resendEmailFetching}
            disabled={user?.is_last_email_confirmed || !isChangingEmailAllowed}
            onClick={resendConfirmationHandler}
            data-test-id="user-resend-email">
            {intl.messages['users.settings.contact-info.primary-email.resend']}
          </Button>
        }
        readOnly={!emailEdit}
        extraRight={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <ExtraInputButton
            type="button"
            palette="muted"
            disabled={(emailEdit && newEmail === user.email) || !isChangingEmailAllowed}
            fetching={newEmailFetching}
            onClick={() => {
              if (emailEdit && !validateEmail(newEmail)) {
                dispatch({
                  type: ActionType.changeEmailError,
                  payload: 'users.settings.email.validation.invalid'
                });
                return;
              }
              if (emailEdit) {
                setEmailHandle(newEmail);
              }
              dispatch({ type: ActionType.emailEditToggle });
            }}
            data-test-id="user-change-email">
            {emailEdit ? <Checkmark /> : <Pencil />}
          </ExtraInputButton>
        }
        label={intl.messages['users.settings.contact-info.primary-email.label']}
        errorMessage={emailEdit ? emailError && intl.formatMessage({ id: emailError }) : ''}
      />
      {emailEdit && (
        <Checkbox onChange={(e) => setSkipConfirmationEmail(e.target.checked)}>
          <CheckboxLabel>
            {intl.messages['users.settings.contact-info.primary-email.checkbox']}
          </CheckboxLabel>
        </Checkbox>
      )}
    </UserFieldWrapper>
  );
};

export default ChangeEmail;
